import { api } from './api'
import { localAnalysis, localFinal, localFreeComment } from './fallback'
import { thumb } from './img'
import { upsertEntry } from './journal'
import { computeMetrics } from './metrics'
import { tr } from '../i18n'
import { checkIn, useSession } from '../store/session'
import type { Stroke } from './types'

let loadTimer: number | null = null

function startLoadMessages(msgs: string[]) {
  const s = useSession.getState()
  let i = 0
  s.set({ loadMsg: msgs[0] })
  stopLoadMessages()
  loadTimer = window.setInterval(() => {
    i = (i + 1) % msgs.length
    useSession.getState().set({ loadMsg: msgs[i] })
  }, 1400)
}

function stopLoadMessages() {
  if (loadTimer) window.clearInterval(loadTimer)
  loadTimer = null
}

/** Холст «до» завершён → ветвление по режиму. */
export async function finishDrawing(png: string, strokes: Stroke[], isPhoto: boolean) {
  const t = tr()
  const s = useSession.getState()
  const metrics = strokes.length ? computeMetrics(strokes) : null

  s.set({
    beforePNG: png,
    strokes,
    metricsBefore: metrics,
    captureMode: isPhoto && !strokes.length ? 'photo' : 'canvas',
    aiFailed: false,
  })

  if (s.mode === 'reply') {
    // ход в диалог: телеметрия уходит сразу, чтобы связать рисунок со штрихами
    s.go('replyq')
    void api
      .telemetry({
        mode: 'reply', captureMode: s.captureMode, checkin: checkIn(), metricsBefore: metrics,
        metricsAfter: null, calmArcs: 0, stateLabel: '', strokesBefore: strokes, strokesAfter: [],
      })
      .then((r) => { if (r) useSession.getState().set({ replySessionId: r.sessionId }) })
    return
  }

  s.go('load')

  if (s.mode === 'therapy') {
    startLoadMessages(t.load.free)
    let analysis
    let failed = false
    try {
      analysis = await api.therapyRead({ img: png, checkin: checkIn(), metrics })
    } catch {
      analysis = localAnalysis(metrics, s.mood)
      failed = true
    }
    stopLoadMessages()
    useSession.getState().set({ analysis, aiFailed: failed, support: !!analysis.support })
    useSession.getState().go('analysis')
    return
  }

  // свободный рисунок
  startLoadMessages(t.load.free)
  let r
  let failed = false
  try {
    r = await api.freeComment({ img: png, metrics })
  } catch {
    r = localFreeComment(metrics)
    failed = true
  }
  stopLoadMessages()

  const st = useSession.getState()
  st.set({ freeText: r.text, freeState: r.state, aiFailed: failed, support: !!r.support })
  await saveEntry({ img: png, state: r.state, type: 'free' })
  st.go('fresult')

  void api.telemetry({
    mode: 'free',
    captureMode: st.captureMode,
    checkin: checkIn(),
    metricsBefore: metrics,
    metricsAfter: null,
    calmArcs: 0,
    stateLabel: r.state,
    strokesBefore: strokes,
    strokesAfter: [],
  })
}

/** Практика завершена → финальный взгляд ИИ, дельта, журнал. */
export async function showResult(afterPNG: string, exStrokes: Stroke[]) {
  const t = tr()
  const s = useSession.getState()
  const metricsEx = exStrokes.length ? computeMetrics(exStrokes) : null
  s.set({ afterPNG, exStrokes, metricsEx, loadMsg: t.load.compare })
  s.go('load')

  let fin
  let failed = false
  try {
    fin = await api.finalRead({
      before: s.beforePNG!,
      after: afterPNG,
      checkin: checkIn(),
      metricsBefore: s.metricsBefore,
      metricsAfter: metricsEx,
      calmArcs: s.calmArcs,
    })
  } catch {
    fin = localFinal(s.metricsBefore, metricsEx, s.calmArcs)
    failed = true
  }

  const st = useSession.getState()
  st.set({ finalText: fin.text, finalState: fin.state, aiFailed: failed, support: st.support || !!fin.support })
  await saveEntry({
    img: afterPNG,
    state: fin.state,
    type: 'therapy',
    deltaSharp: st.metricsBefore ? st.metricsBefore.sharpPct - (metricsEx?.sharpPct ?? 0) : null,
  })
  st.go('result')

  void api.telemetry({
    mode: 'therapy',
    captureMode: st.captureMode,
    checkin: checkIn(),
    metricsBefore: st.metricsBefore,
    metricsAfter: metricsEx,
    calmArcs: st.calmArcs,
    stateLabel: fin.state,
    strokesBefore: st.strokes,
    strokesAfter: exStrokes,
  })
}

export async function saveEntry(p: {
  img: string
  state: string
  type: 'free' | 'therapy'
  deltaSharp?: number | null
}) {
  const s = useSession.getState()
  try {
    const img = await thumb(p.img, 420)
    const id = s.entryId ?? Date.now()
    upsertEntry({
      id,
      d: Date.now(),
      img,
      state: p.state,
      type: p.type,
      mood: s.mood,
      tags: s.tags,
      post: s.postDraft || '',
      deltaSharp: p.deltaSharp ?? null,
    })
    s.set({ entryId: id })
  } catch {
    /* журнал не критичен для сессии */
  }
}

/** Контекст сессии для свободных вопросов к ИИ. */
export function sessionContext(): string {
  const s = useSession.getState()
  const mb = s.metricsBefore
  const me = s.metricsEx
  return [
    `check-in: mood ${s.mood}/9, energy ${s.energy}/9; tags: ${s.tags.join(', ') || '—'}; own words: "${s.feelText || '—'}"`,
    `first read: "${s.analysis?.analysis || '—'}" focus: "${s.analysis?.focus || '—'}"`,
    `in rhythm: ${s.calmArcs}; sharp turns ${mb ? mb.sharpPct + '%' : '—'} → ${me?.sharpPct ?? '—'}%; raggedness ${mb?.speedCV ?? '—'} → ${me?.speedCV ?? '—'}`,
    `final read: "${s.finalText || '—'}"; label: "${s.finalState || s.freeState || '—'}"`,
  ].join('\n')
}
